import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { normalizeEgyptianPhoneNumber, formatEgyptianPhoneNumber } from '../../utils/phoneUtils';

interface EgyptianPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  placeholder?: string;
}

export const EgyptianPhoneInput: React.FC<EgyptianPhoneInputProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  id = 'phoneNumber',
  label = 'رقم الهاتف',
  placeholder = '01234567890',
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [suggestion, setSuggestion] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    onChange(inputValue);

    // Clear suggestion initially
    setSuggestion('');

    // Don't show suggestions if input is empty or very short
    if (!inputValue.trim() || inputValue.length < 4) {
      return;
    }

    // Try to normalize and suggest
    const normalizeResult = normalizeEgyptianPhoneNumber(inputValue);
    
    if (normalizeResult.success && normalizeResult.phoneNumber) {
      const formatted = formatEgyptianPhoneNumber(normalizeResult.phoneNumber);
      if (formatted !== inputValue) {
        setSuggestion(`هل تقصد: ${formatted}؟`);
      }
    } else if (inputValue.length >= 8) {
      // Provide helpful suggestions based on common patterns
      if (/^\d{10,11}$/.test(inputValue.replace(/\s/g, ''))) {
        if (inputValue.startsWith('0')) {
          setSuggestion('سيتم تحويل الرقم تلقائياً إلى التنسيق الدولي');
        } else if (inputValue.startsWith('1')) {
          setSuggestion('سيتم إضافة رمز الدولة (+20) تلقائياً');
        }
      }
    }
  };

  const handleBlur = () => {
    // Auto-format on blur if possible
    const normalizeResult = normalizeEgyptianPhoneNumber(displayValue);
    if (normalizeResult.success && normalizeResult.phoneNumber) {
      const formatted = formatEgyptianPhoneNumber(normalizeResult.phoneNumber);
      setDisplayValue(formatted);
      onChange(normalizeResult.phoneNumber); // Store the normalized version
      setSuggestion('');
    }
  };

  const applySuggestion = () => {
    const normalizeResult = normalizeEgyptianPhoneNumber(displayValue);
    if (normalizeResult.success && normalizeResult.phoneNumber) {
      const formatted = formatEgyptianPhoneNumber(normalizeResult.phoneNumber);
      setDisplayValue(formatted);
      onChange(normalizeResult.phoneNumber);
      setSuggestion('');
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="tel"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        dir="ltr"
        className="text-left"
        disabled={disabled}
      />
      
      {suggestion && !error && (
        <div className="flex items-center justify-between text-xs text-blue-600 bg-blue-50 p-2 rounded">
          <span>{suggestion}</span>
          {suggestion.includes('هل تقصد:') && (
            <button
              type="button"
              onClick={applySuggestion}
              className="text-blue-700 underline hover:no-underline"
            >
              نعم
            </button>
          )}
        </div>
      )}
      
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
      
      {!error && !suggestion && (
        <p className="text-xs text-muted-foreground">
          أدخل رقم هاتفك المصري (مثال: 01234567890)
        </p>
      )}
    </div>
  );
};
